import axios from "axios";

// Mirrors CpSourcingApis.js's BASE pattern — same env var, same backend.
// Sales-mode-only build: CRM mode, Transfer by Project, Rearrange a Team, and
// CP-to-CP Transfer API calls live in the full (non-sales-only) branch
// instead. Update CP Sourcing Manager is built here.
const BASE = `${process.env.REACT_APP_PAYMENT_LINK_URL}api/work-transfer`;

// "Who's work is moving" picker: Sales/CRM users who currently own at least
// one lead — both active and disabled (a disabled user's leads are exactly
// what this screen exists to hand off).
export const getEligibleSourceUsers = async () => {
  const res = await axios.get(`${BASE}/move-work/eligible-source-users`);
  return res.data; // {success, data:{users}}
};

// "Assign to" / recipient picker: active Sales/CRM users — no "already owns
// work" requirement.
export const getEligibleRecipientUsers = async () => {
  const res = await axios.get(`${BASE}/move-work/eligible-recipient-users`);
  return res.data; // {success, data:{users}}
};

// "Who's work is moving" overview tiles (2026-08-19) — the one count that
// isn't derivable from the already-loaded eligible-source-users list (users
// who own zero leads but still have a pending approval sitting with them).
export const getMoveWorkSourceOverview = async () => {
  const res = await axios.get(`${BASE}/move-work/source-overview`);
  return res.data; // {success, data:{zeroLeadsPendingApprovalsCount}}
};

// Phase 2 — "Move someone's work": bucket preview for a user. "Self + team"
// scope was removed entirely (2026-08-26) — always the selected user's own
// leads now, never a manager's direct reports too.
export const getMoveWorkBuckets = async ({ userId, dateFrom, dateTo, includeCrmTagged, includeAgreementDone }) => {
  const params = { userId };
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  // Allotment bucket's own opt-in toggles (2026-08-25) — omitted entirely
  // rather than sent as "false" so the backend's own defaults (both
  // excluded) are the single source of truth, not duplicated here.
  if (includeCrmTagged) params.includeCrmTagged = "true";
  if (includeAgreementDone) params.includeAgreementDone = "true";
  const res = await axios.get(`${BASE}/move-work/buckets`, { params });
  return res.data; // {success, data:{buckets, totalEligible, unbucketedStatusesSeen}}
};

// Move-someone's-work (Sales mode) pending-approval check — run before Review
// opens. Not scoped by bucket/leads — returns every approval currently
// sitting with this person, bifurcated into its three real kinds:
// Cancellation and Unit Shift are pending with a specific stored user id,
// regardless of lead ownership; Site Visit has no such column — its
// "pending with X" is computed live from Leads.userParentId instead.
export const getMoveWorkPendingApprovals = async ({ userId }) => {
  const res = await axios.get(`${BASE}/move-work/pending-approvals`, { params: { userId } });
  return res.data; // {success, data:{cancelRequests, changeUnitRequests, siteVisitApprovals}}
};

// Intent-based confirm call — the frontend sends "what to do" (buckets,
// recipients), the backend builds the raw operations internally. Preferred
// over the generic /execute for the built-in entry points.
export const executeMoveWorkFlow = async ({
  userId,
  scope,
  dateFrom,
  dateTo,
  bucketNames,
  excludedLeadIds,
  recipients,
  distribution,
  reason,
  performedBy,
  cancelRequestIds,
  changeUnitRequestIds,
  siteVisitApprovalIds,
}) => {
  const res = await axios.post(`${BASE}/move-work/execute`, {
    userId,
    scope,
    dateFrom,
    dateTo,
    bucketNames,
    excludedLeadIds,
    recipients,
    distribution,
    reason,
    performedBy,
    cancelRequestIds,
    changeUnitRequestIds,
    siteVisitApprovalIds,
  });
  return res.data; // {success, data:{transferBatchId, operationsApplied}} or {success:false, error, message, details}
};

// FR-12 — block sequencing. Called by the EXISTING Block button in
// UsersPage.jsx before it deactivates a user.
export const getBlockCheck = async (userId) => {
  const res = await axios.get(`${BASE}/block-check/${userId}`);
  return res.data; // {success, data:{hasActiveWork, summary:{leadsCount, leadBuckets, directReportsCount}}}
};

// Phase 6 — FR-16/17/18 audit/history reads.
export const getAllTransferHistory = async ({ limit, offset } = {}) => {
  const res = await axios.get(`${BASE}/history/all`, { params: { limit, offset } });
  return res.data; // {success, data:{rows, count}}
};

export const getUserTransferHistory = async (userId) => {
  const res = await axios.get(`${BASE}/history/user/${userId}`);
  return res.data; // {success, data:{rows}}
};

export const getProjectTransferHistory = async (projectId) => {
  const res = await axios.get(`${BASE}/history/project/${projectId}`);
  return res.data;
};

export const getCpSourceTransferHistory = async (cpId) => {
  const res = await axios.get(`${BASE}/history/cp-source/${cpId}`);
  return res.data;
};

export const getBatchTransferHistory = async (transferBatchId) => {
  const res = await axios.get(`${BASE}/history/batch/${transferBatchId}`);
  return res.data;
};

// "Update CP Sourcing Manager" — source/recipient pickers are the CP
// Sourcing Manager persona (Users.isCpSourcingUser), NOT Sales/CRM, so these
// deliberately don't reuse getEligibleSourceUsers/getEligibleRecipientUsers
// above. Source is not active-only (a departed/blocked manager's CPs are
// exactly what this hands off); recipient is active-only.
export const getCpSourcingSourceUsers = async () => {
  const res = await axios.get(`${BASE}/cp-sourcing-manager/source-users`);
  return res.data; // {success, data:{users}}
};

export const getCpSourcingRecipientUsers = async () => {
  const res = await axios.get(`${BASE}/cp-sourcing-manager/recipient-users`);
  return res.data; // {success, data:{users}}
};

// Task #1's picker: approved CPs currently managed by userId, filterable by
// cpType / created_at range / free-text search on billingName or cpDisplayId.
export const getEligibleCpsForSourcingManager = async ({ userId, cpType, dateFrom, dateTo, search }) => {
  const params = { userId };
  if (cpType?.length) params.cpType = cpType.join(",");
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (search) params.search = search;
  const res = await axios.get(`${BASE}/cp-sourcing-manager/eligible-cps`, { params });
  return res.data; // {success, data:{cps}}
};

export const executeCpSourcingManagerChangeFlow = async ({ fromUserId, toUserId, cpIds, reason, performedBy }) => {
  const res = await axios.post(`${BASE}/cp-sourcing-manager/execute`, { fromUserId, toUserId, cpIds, reason, performedBy });
  return res.data; // {success, data:{transferBatchId, operationsApplied}} or {success:false, error, message, details}
};
